import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { supabase } from '../supabaseClient';
import '../styles/SavedReports.css';
import { FaTimes, FaEdit, FaCopy } from 'react-icons/fa';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, StyleSheet } from '@react-pdf/renderer';
import { createEditor } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import { Node } from 'slate';
import { useNavigate } from 'react-router-dom';
import SOAPView from './SOAPView';
import { loadNoteIntoQuickSOAP } from '../utils/cases';
import usePagedList from '../hooks/usePagedList';

const mainHeaders = [
    'Veterinary Medical Record:',
    'Patient Information:',
    'Client Information:',
    'Presenting Complaint:',
    'History:',
    'Physical Exam Findings:',
    'Diagnostic Tests:',
    'Assessment:',
    'Diagnosis:',
    'Differential Diagnoses:',
    'PLAN',
    'Treatment:',
    'Monitoring:',
    'Drug Interactions/Side Effects:',
    'Naturopathic Medicine:',
    'Client Communications:',
    'Follow-Up:',
    'Patient Visit Summary:',
    'Notes:'
];

const PDFDocument = ({ reportText }) => {
    const styles = StyleSheet.create({
        page: {
            padding: 40,
            fontSize: 12,
            fontFamily: 'Helvetica',
            lineHeight: 1.2
        },
        text: {
            marginBottom: 0,
            whiteSpace: 'pre-wrap',
            fontFamily: 'Helvetica'
        },
        strongText: {
            fontFamily: 'Helvetica-Bold',
            marginBottom: 0,
            fontSize: 12
        },
        lineBreak: {
            height: 8
        }
    });

    // Split content into main content and summary sections
    const splitContent = (text) => {
        const lines = text.split('\n');
        const summaryIndex = lines.findIndex(line =>
            line.includes('**Patient Visit Summary:**') ||
            line.includes('**Notes:**')
        );

        if (summaryIndex === -1) return { mainContent: text, summaryContent: '' };

        const mainContent = lines.slice(0, summaryIndex).join('\n');
        const summaryContent = lines.slice(summaryIndex).join('\n');
        return { mainContent, summaryContent };
    };

    const renderContent = (content) => {
        if (!content) return null;
        const lines = content.split('\n');
        return lines.map((line, index) => {
            const trimmedLine = line.trim();
            if (!trimmedLine) {
                return <Text key={`break-${index}`} style={styles.lineBreak}>{'\n'}</Text>;
            }
            if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                return (
                    <Text key={index} style={styles.strongText}>
                        {trimmedLine.slice(2, -2)}
                        {'\n'}
                    </Text>
                );
            }
            return (
                <Text key={index} style={styles.text}>
                    {line}
                    {'\n'}
                </Text>
            );
        });
    };

    const { mainContent, summaryContent } = splitContent(reportText);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {renderContent(mainContent)}
            </Page>
            {summaryContent && (
                <Page size="A4" style={styles.page}>
                    {renderContent(summaryContent)}
                </Page>
            )}
        </Document>
    );
};

const PDFButton = ({ reportText, reportName }) => {
    const handlePDFDownload = async () => {
        try {
            const doc = <PDFDocument reportText={reportText} />;
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${reportName || 'report'}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF error:', error);
        }
    };

    return (
        <button className="copy-button" onClick={handlePDFDownload}>
            Download PDF
        </button>
    );
};

const PrintButton = ({ reportText }) => {
    const handlePrint = async () => {
        try {
            const doc = <PDFDocument reportText={reportText} />;
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);

            const printWindow = window.open(url, '_blank');
            printWindow.onload = () => {
                printWindow.print();
                printWindow.onafterprint = () => {
                    printWindow.close();
                    URL.revokeObjectURL(url);
                };
            };
        } catch (error) {
            console.error('Print error:', error);
        }
    };

    return (
        <button className="copy-button" onClick={handlePrint}>
            Print Report
        </button>
    );
};

const deserializeSlateValue = (text) => {
    if (!text) return [{ type: 'paragraph', children: [{ text: '' }] }];

    const paragraphs = text.split('\n');
    return paragraphs.map(paragraph => {
        let trimmedParagraph = paragraph.trim();

        // Handle headers with ** markers
        if (trimmedParagraph.startsWith('**') && trimmedParagraph.endsWith('**')) {
            const cleanHeader = trimmedParagraph.replace(/\*\*/g, '');
            return {
                type: 'heading',
                children: [{ text: cleanHeader, bold: true }]
            };
        }

        // Handle numbered diagnoses
        if (/^\*\*\d+\..+\*\*$/.test(trimmedParagraph)) {
            const cleanText = trimmedParagraph.replace(/\*\*/g, '');
            return {
                type: 'paragraph',
                children: [{ text: cleanText, bold: true }]
            };
        }

        // Handle DDx lines
        if (trimmedParagraph.startsWith('DDx:')) {
            return {
                type: 'paragraph',
                children: [{ text: trimmedParagraph }]
            };
        }

        return {
            type: 'paragraph',
            children: [{ text: trimmedParagraph }]
        };
    });
};

const createSlateValue = (text) => {
    // Split text into paragraphs
    const paragraphs = text.split('\n');

    return paragraphs.map(paragraph => {
        // Check for headers
        if (mainHeaders.some(header =>
            paragraph.trim() === header ||
            paragraph.startsWith(header)
        )) {
            return {
                type: 'heading',
                children: [{
                    text: paragraph,
                    bold: true
                }]
            };
        }

        // Check for indented lines
        if ((paragraph.includes('•') ||
            (paragraph.includes('-') &&
                !paragraph.includes('Date:') &&
                !paragraph.includes('Signature:'))) &&
            !paragraph.trim().match(/^\d+[\.\)]/)) {
            return {
                type: 'indented',
                children: [{ text: paragraph }]
            };
        }

        // Default paragraph
        return {
            type: 'paragraph',
            children: [{ text: paragraph }]
        };
    });
};

const serializeSlateValue = (nodes) => {
    return nodes
        .map(n => Node.string(n))
        .join('\n');
};

const isHeader = text => {
    return text.startsWith('**') && text.endsWith(':**');
};

const renderElement = props => {
    const { element, children, attributes } = props;

    if (element.type === 'heading') {
        return (
            <div
                {...attributes}
                style={{
                    fontWeight: 'bold',
                    lineHeight: '1.2',
                    fontSize: '14px'
                }}
            >
                {children}
            </div>
        );
    }

    return (
        <div
            {...attributes}
            style={{
                lineHeight: '1.2',
                fontSize: '14px'
            }}
        >
            {children}
        </div>
    );
};

const renderLeaf = props => {
    const { attributes, children, leaf } = props;
    return (
        <span
            {...attributes}
            style={{
                fontWeight: leaf.bold ? 'bold' : 'normal'
            }}
        >
            {children}
        </span>
    );
};

const processSlateForPDF = (nodes) => {
    let formattedText = '';
    let previousWasEmpty = false;

    nodes.forEach((node) => {
        const text = Node.string(node);
        const trimmedText = text.trim();

        if (!trimmedText) {
            if (!previousWasEmpty) {
                formattedText += '\n';
            }
            previousWasEmpty = true;
            return;
        }
        previousWasEmpty = false;

        const isBold = node.type === 'heading' ||
            node.children[0]?.bold ||
            trimmedText.startsWith('**') && trimmedText.endsWith('**');

        if (isBold) {
            const cleanText = trimmedText.replace(/^\*\*|\*\*$/g, '');
            formattedText += `**${cleanText}**\n`;
        } else {
            formattedText += `${text}\n`;
        }
    });

    return formattedText;
};

// Parse SOAP report in QuickSOAP format (removes markdown, finds sections by name)
const parseSOAPReport = (text) => {
    if (!text) return { sections: [] };

    // Remove markdown formatting
    let cleanText = text
        .replace(/\*\*/g, '') // Remove bold markers
        .replace(/#{1,6}\s*/g, '') // Remove headers
        .replace(/###\s*/g, '')
        .replace(/##\s*/g, '')
        .replace(/#\s*/g, '')
        .trim();

    const sections = [];
    const sectionNames = ['Subjective', 'Objective', 'Assessment', 'Plan'];

    // Try to find sections by name
    sectionNames.forEach(sectionName => {
        // Look for section header (case insensitive) - handle both "S - Subjective" and "Subjective:" formats
        const regex = new RegExp(`(?:^|\\n)\\s*(?:[SOAP]\\s*-\\s*)?${sectionName}:?\\s*\\n?`, 'gi');
        const match = cleanText.match(regex);

        if (match) {
            const startIndex = cleanText.search(regex);
            const sectionStart = startIndex + match[0].length;

            // Find the next section or end of text
            let sectionEnd = cleanText.length;
            for (let i = 0; i < sectionNames.length; i++) {
                if (sectionNames[i].toLowerCase() !== sectionName.toLowerCase()) {
                    const nextRegex = new RegExp(`(?:^|\\n)\\s*(?:[SOAP]\\s*-\\s*)?${sectionNames[i]}:?\\s*\\n?`, 'gi');
                    const nextMatch = cleanText.substring(sectionStart).search(nextRegex);
                    if (nextMatch !== -1) {
                        sectionEnd = sectionStart + nextMatch;
                        break;
                    }
                }
            }

            const content = cleanText.substring(sectionStart, sectionEnd).trim();
            if (content) {
                sections.push({
                    name: sectionName,
                    content: content
                });
            }
        }
    });

    // If no sections found, try simpler parsing
    if (sections.length === 0) {
        const lines = cleanText.split('\n');
        let currentSection = null;
        let currentContent = [];

        lines.forEach(line => {
            const trimmed = line.trim();
            const sectionMatch = sectionNames.find(name => {
                const lowerName = name.toLowerCase();
                return trimmed.toLowerCase().startsWith(lowerName + ':') ||
                    trimmed.toLowerCase() === lowerName ||
                    trimmed.toLowerCase().startsWith(lowerName.charAt(0) + ' –') ||
                    trimmed.toLowerCase().startsWith(lowerName.charAt(0) + ' -');
            });

            if (sectionMatch) {
                if (currentSection) {
                    sections.push({
                        name: currentSection,
                        content: currentContent.join('\n').trim()
                    });
                }
                currentSection = sectionMatch;
                currentContent = [];
            } else if (currentSection && trimmed) {
                currentContent.push(trimmed);
            }
        });

        if (currentSection) {
            sections.push({
                name: currentSection,
                content: currentContent.join('\n').trim()
            });
        }
    }

    // Sort sections in SOAP order
    const sectionOrder = ['Subjective', 'Objective', 'Assessment', 'Plan'];
    sections.sort((a, b) => {
        const indexA = sectionOrder.indexOf(a.name);
        const indexB = sectionOrder.indexOf(b.name);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return { sections, rawText: cleanText };
};

// Get section colors (same as QuickSOAP)
const getSectionColor = (sectionName) => {
    const name = sectionName.toLowerCase();
    if (name.includes('subjective')) return {
        border: '#3b82f6',
        header: 'bg-gradient-to-r from-blue-500 to-blue-700',
        bg: 'bg-blue-50'
    };
    if (name.includes('objective')) return {
        border: '#10b981',
        header: 'bg-gradient-to-r from-green-500 to-green-700',
        bg: 'bg-green-50'
    };
    if (name.includes('assessment')) return {
        border: '#f59e0b',
        header: 'bg-gradient-to-r from-amber-500 to-amber-700',
        bg: 'bg-amber-50'
    };
    if (name.includes('plan')) return {
        border: '#ef4444',
        header: 'bg-gradient-to-r from-red-500 to-red-700',
        bg: 'bg-red-50'
    };
    return {
        border: '#6b7280',
        header: 'bg-gradient-to-r from-gray-500 to-gray-700',
        bg: 'bg-gray-50'
    };
};

const parseReportForSOAP = (reportText) => {
    if (!reportText) return null;

    const lines = reportText.split('\n');
    const sections = {
        subjective: [],
        objective: [],
        assessment: [],
        plan: []
    };

    let currentSection = null;
    let currentSubsection = null;

    for (let line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        // Check for main headers and map to SOAP sections
        if (trimmedLine.includes('**Patient Information:**') ||
            trimmedLine.includes('**Staff:**') ||
            trimmedLine.includes('**Presenting Complaint:**') ||
            trimmedLine.includes('**History:**')) {
            currentSection = 'subjective';
            currentSubsection = trimmedLine;
            sections.subjective.push({ header: trimmedLine, content: [] });
        } else if (trimmedLine.includes('**Physical Exam Findings:**') ||
            trimmedLine.includes('**Diagnostic Tests:**')) {
            currentSection = 'objective';
            currentSubsection = trimmedLine;
            sections.objective.push({ header: trimmedLine, content: [] });
        } else if (trimmedLine.includes('**Assessment:**') ||
            trimmedLine.includes('**Diagnosis:**') ||
            trimmedLine.includes('**Differential Diagnosis:**')) {
            currentSection = 'assessment';
            currentSubsection = trimmedLine;
            sections.assessment.push({ header: trimmedLine, content: [] });
        } else if (trimmedLine.includes('**Treatment:**') ||
            trimmedLine.includes('**Monitoring:**') ||
            trimmedLine.includes('**Naturopathic Medicine:**') ||
            trimmedLine.includes('**Client Communications:**') ||
            trimmedLine.includes('**Follow-Up:**') ||
            trimmedLine.includes('**Plan:**') ||
            trimmedLine.includes('**Patient Visit Summary:**') ||
            trimmedLine.includes('**Notes:**')) {
            currentSection = 'plan';
            currentSubsection = trimmedLine;
            sections.plan.push({ header: trimmedLine, content: [] });
        } else if (currentSection && sections[currentSection].length > 0) {
            // Add content to current subsection
            const lastSubsection = sections[currentSection][sections[currentSection].length - 1];
            lastSubsection.content.push(line);
        }
    }

    return sections;
};

const SavedReports = () => {
    const { user, isAuthenticated, isLoading } = useAuth0();
    const [selectedReport, setSelectedReport] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newReportName, setNewReportName] = useState('');
    const [error, setError] = useState(null);
    const [editor] = useState(() => withHistory(withReact(createEditor())));
    const [searchTerm, setSearchTerm] = useState('');
    const [editingReport, setEditingReport] = useState(null);
    const [copyButtonText, setCopyButtonText] = useState('Copy to Clipboard');
    const [currentView, setCurrentView] = useState('soap'); // Default to SOAP view
    const [hasNewMobileSOAP, setHasNewMobileSOAP] = useState(false);
    const [newMobileSOAPId, setNewMobileSOAPId] = useState(null);
    const [hasNewDesktopQuickSOAP, setHasNewDesktopQuickSOAP] = useState(false);
    const [newDesktopQuickSOAPId, setNewDesktopQuickSOAPId] = useState(null);
    const [filterType, setFilterType] = useState(() => {
        // Load from localStorage or default to 'all'
        const savedFilter = localStorage.getItem('savedReportsFilterType');
        return savedFilter || 'all';
    });
    const navigate = useNavigate();
    const {
        items: reports,
        setItems: setReports,
        loading: isLoadingReports,
        loadingMore,
        hasMore,
        loadMore,
        searching,
        error: listError,
        reload: reloadReports,
    } = usePagedList('records', { q: searchTerm, type: filterType, enabled: isAuthenticated && !!user });

    // Save filter type to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('savedReportsFilterType', filterType);
    }, [filterType]);

    useEffect(() => {
        if (!isAuthenticated || !user) return;
        if (localStorage.getItem('hasNewDesktopQuickSOAP') === 'true') {
            setHasNewDesktopQuickSOAP(true);
            const newDesktopQuickSOAPIdFromStorage = localStorage.getItem('newDesktopQuickSOAPId');
            if (newDesktopQuickSOAPIdFromStorage) {
                setNewDesktopQuickSOAPId(newDesktopQuickSOAPIdFromStorage);
            }
        }

        // Clear sidebar notification when SavedReports component is viewed
        setHasNewMobileSOAP(false);
        localStorage.removeItem('hasNewMobileSOAP');
        // Clear site-wide notification count
        localStorage.removeItem('mobileSOAPCount');
        // Dispatch event to Dashboard to clear sidebar and site-wide notifications
        window.dispatchEvent(new CustomEvent('clearMobileSOAPNotification'));

        // Note: Don't clear desktop QuickSOAP notification here - let user dismiss it manually
    }, [isAuthenticated, user]);

    // Check for new mobile SOAP on mount and listen for events
    useEffect(() => {
        // Check localStorage on mount
        const hasNewSOAP = localStorage.getItem('hasNewMobileSOAP') === 'true';
        const newSOAPId = localStorage.getItem('newMobileSOAPId');
        if (hasNewSOAP && newSOAPId) {
            setHasNewMobileSOAP(true);
            setNewMobileSOAPId(newSOAPId);
        }

        // Check for new desktop QuickSOAP on mount
        const hasNewDesktopQuickSOAPFlag = localStorage.getItem('hasNewDesktopQuickSOAP') === 'true';
        const newDesktopQuickSOAPIdFromStorage = localStorage.getItem('newDesktopQuickSOAPId');
        if (hasNewDesktopQuickSOAPFlag) {
            setHasNewDesktopQuickSOAP(true);
            if (newDesktopQuickSOAPIdFromStorage) {
                setNewDesktopQuickSOAPId(newDesktopQuickSOAPIdFromStorage);
            }
        }

        // Listen for new mobile SOAP events
        const handleNewMobileSOAP = (event) => {
            const reportId = event.detail?.reportId;
            if (reportId) {
                setHasNewMobileSOAP(true);
                setNewMobileSOAPId(reportId);
                localStorage.setItem('hasNewMobileSOAP', 'true');
                localStorage.setItem('newMobileSOAPId', reportId);
                // Refresh reports to show the new one
                reloadReports();
            }
        };

        // Listen for new desktop QuickSOAP events
        const handleNewDesktopQuickSOAP = (event) => {
            const reportId = event.detail?.reportId;
            if (reportId) {
                setHasNewDesktopQuickSOAP(true);
                setNewDesktopQuickSOAPId(reportId);
                // Store in localStorage so it persists even if component unmounts
                localStorage.setItem('hasNewDesktopQuickSOAP', 'true');
                localStorage.setItem('newDesktopQuickSOAPId', reportId);
                // Refresh reports to show the new one
                reloadReports();
            }
        };

        window.addEventListener('newMobileSOAPGenerated', handleNewMobileSOAP);
        window.addEventListener('newDesktopQuickSOAPGenerated', handleNewDesktopQuickSOAP);

        return () => {
            window.removeEventListener('newMobileSOAPGenerated', handleNewMobileSOAP);
            window.removeEventListener('newDesktopQuickSOAPGenerated', handleNewDesktopQuickSOAP);
        };
    }, [isAuthenticated, user, reloadReports]);

    // Check for unopened desktop QuickSOAP reports after reports are fetched
    // This is for visual indicators only - notification banner is controlled by localStorage flag
    useEffect(() => {
        if (reports.length > 0 && isAuthenticated) {
            const unopenedDesktopReports = JSON.parse(localStorage.getItem('unopenedDesktopQuickSOAPReports') || '[]');
            const viewedReports = JSON.parse(localStorage.getItem('viewedDesktopQuickSOAPReports') || '[]');
            const hasUnopened = reports.some(r => {
                const recordType = getRecordType(r);
                return recordType === 'quicksoap' && !r.form_data?.from_mobile && unopenedDesktopReports.includes(r.id) && !viewedReports.includes(r.id);
            });
            // Only set notification banner if localStorage flag is set (don't clear it here)
            const hasNotificationFlag = localStorage.getItem('hasNewDesktopQuickSOAP') === 'true';
            if (hasNotificationFlag || hasUnopened) {
                setHasNewDesktopQuickSOAP(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reports.length, isAuthenticated]);

    const handleReportClick = (report) => {
        // Check if this is a QuickSOAP, Summary, or Callback report - if so, auto-load it instead of showing preview
        const recordType = getRecordType(report);
        if (recordType === 'quicksoap' || recordType === 'soap' || recordType === 'summary' || recordType === 'callback') {
            // Mark mobile report as viewed when clicked
            if (report.form_data?.from_mobile === true) {
                const viewedReports = JSON.parse(localStorage.getItem('viewedMobileSOAPReports') || '[]');
                if (!viewedReports.includes(report.id)) {
                    viewedReports.push(report.id);
                    localStorage.setItem('viewedMobileSOAPReports', JSON.stringify(viewedReports));
                }
            } else {
                // Mark desktop QuickSOAP/Summary/Callback as viewed when clicked
                const viewedReports = JSON.parse(localStorage.getItem('viewedDesktopQuickSOAPReports') || '[]');
                if (!viewedReports.includes(report.id)) {
                    viewedReports.push(report.id);
                    localStorage.setItem('viewedDesktopQuickSOAPReports', JSON.stringify(viewedReports));
                    // Remove from unopened list
                    const unopenedReports = JSON.parse(localStorage.getItem('unopenedDesktopQuickSOAPReports') || '[]');
                    const updatedUnopened = unopenedReports.filter(id => id !== report.id);
                    localStorage.setItem('unopenedDesktopQuickSOAPReports', JSON.stringify(updatedUnopened));
                    // Update notification state
                    if (updatedUnopened.length === 0) {
                        setHasNewDesktopQuickSOAP(false);
                    }
                }
            }
            // Auto-load into QuickSOAP view
            handleLoadQuickSOAP(report);
            return;
        }
        
        // For PetSOAP/Generator reports, show preview as before
        setSelectedReport(selectedReport === report ? null : report);
        
        // Mark mobile report as viewed when clicked
        if (report.form_data?.from_mobile === true) {
            const viewedReports = JSON.parse(localStorage.getItem('viewedMobileSOAPReports') || '[]');
            if (!viewedReports.includes(report.id)) {
                viewedReports.push(report.id);
                localStorage.setItem('viewedMobileSOAPReports', JSON.stringify(viewedReports));
            }
        }
        
        // Set default view based on record type: SOAP for Generator records
        if (selectedReport !== report) {
            if (recordType === 'generator') {
                setCurrentView('soap');
            } else {
                setCurrentView('standard');
            }
        }
        
        setTimeout(() => {
            const content = document.querySelector('.report-content');
            if (content) {
                content.classList.toggle('expanded', selectedReport !== report);
            }
        }, 50);
    };
    
    // Helper function to check if a mobile report is unopened
    const isUnopenedMobileReport = (report) => {
        if (!report.form_data?.from_mobile) return false;
        const viewedReports = JSON.parse(localStorage.getItem('viewedMobileSOAPReports') || '[]');
        return !viewedReports.includes(report.id);
    };

    // Helper function to check if a desktop QuickSOAP report is unopened
    const isUnopenedDesktopQuickSOAP = (report) => {
        const recordType = getRecordType(report);
        if (recordType !== 'quicksoap' || report.form_data?.from_mobile) return false;
        const unopenedReports = JSON.parse(localStorage.getItem('unopenedDesktopQuickSOAPReports') || '[]');
        const viewedReports = JSON.parse(localStorage.getItem('viewedDesktopQuickSOAPReports') || '[]');
        return unopenedReports.includes(report.id) && !viewedReports.includes(report.id);
    };

    const handleDeleteReport = async (id) => {
        if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
            return;
        }

        try {
            const { error } = await supabase
                .from('saved_reports')
                .delete()
                .eq('id', id);

            if (error) throw error;

            // Update local reports state
            setReports(reports.filter(report => report.id !== id));
            setSelectedReport(null);
        } catch (error) {
            setError("Failed to delete report. Please try again.");
            console.error("Error deleting report:", error);
        }
    };

    const handleEditClick = (index) => {
        setEditingIndex(index);
        setNewReportName(reports[index].report_name);
    };

    const handleNameChange = (e) => {
        setNewReportName(e.target.value);
    };

    // Helper function to detect record type
    const getRecordType = (report) => {
        // First check if record_type column exists (if migration was run)
        if (report.record_type) {
            return report.record_type;
        }

        // Fallback: detect from form_data structure
        if (!report.form_data) {
            // Legacy records without form_data are assumed to be generator records
            return 'generator';
        }

        const formData = report.form_data;
        
        // Check for Summary record
        if (formData.record_type === 'summary') {
            return 'summary';
        }
        
        // Check for Callback record
        if (formData.record_type === 'callback') {
            return 'callback';
        }
        
        // Check for QuickSOAP record (soap type or legacy quicksoap)
        if (formData.record_type === 'soap' || 
            formData.record_type === 'quicksoap' || 
            (formData.dictations && formData.input !== undefined)) {
            return 'quicksoap';
        }
        
        // Check for Generator record
        if (formData.record_type === 'generator' || 
            formData.patientName || 
            formData.species || 
            formData.presentingComplaint) {
            return 'generator';
        }

        // Default to generator for unknown types
        return 'generator';
    };

    const handleNameBlur = async (id) => {
        try {
            const { error } = await supabase
                .from('saved_reports')
                .update({ report_name: newReportName })
                .eq('id', id);

            if (error) throw error;

            // Update local state with new report name
            setReports(reports.map(report =>
                report.id === id ? { ...report, report_name: newReportName } : report
            ));
            setEditingIndex(null);
        } catch (error) {
            setError("Failed to update report name. Please try again.");
            console.error("Error updating report name:", error);
        }
    };


    const handleSaveEdit = async (report) => {
        try {
            const { error } = await supabase
                .from('saved_reports')
                .update({ report_text: report.report_text })
                .eq('id', report.id);

            if (error) throw error;

            // Update local state - both reports list and selected report
            setReports(reports.map(r =>
                r.id === report.id ? { ...r, report_text: report.report_text } : r
            ));

            // Update selected report to reflect the changes
            setSelectedReport(prev => ({
                ...prev,
                report_text: report.report_text
            }));

            setEditingReport(null);

        } catch (error) {
            setError("Failed to save changes");
            console.error("Error updating report:", error);
        }
    };

    const handleCancelEdit = () => {
        setEditingReport(null);
    };

    const handleCloseReport = () => {
        setEditingReport(null);
        setSelectedReport(null);
    };

    const copyToClipboard = () => {
        // Use the current text (editing if in edit mode, otherwise selected)
        const currentText = editingReport?.id === selectedReport.id ?
            editingReport.report_text : selectedReport.report_text;

        // Parse into SOAP sections
        const soapData = parseReportForSOAP(currentText);

        let organizedContent = '';
        if (soapData && (soapData.subjective.length > 0 || soapData.objective.length > 0 || soapData.assessment.length > 0 || soapData.plan.length > 0)) {
            // Organize by SOAP sections
            if (soapData.subjective.length > 0) {
                organizedContent += 'SUBJECTIVE\n\n';
                organizedContent += soapData.subjective.map(sub => {
                    const header = sub.header.replace(/\*\*/g, '');
                    const content = sub.content.join('\n');
                    return `${header}\n${content}`;
                }).join('\n\n') + '\n\n';
            }

            if (soapData.objective.length > 0) {
                organizedContent += 'OBJECTIVE\n\n';
                organizedContent += soapData.objective.map(sub => {
                    const header = sub.header.replace(/\*\*/g, '');
                    const content = sub.content.join('\n');
                    return `${header}\n${content}`;
                }).join('\n\n') + '\n\n';
            }

            if (soapData.assessment.length > 0) {
                organizedContent += 'ASSESSMENT\n\n';
                organizedContent += soapData.assessment.map(sub => {
                    const header = sub.header.replace(/\*\*/g, '');
                    const content = sub.content.join('\n');
                    return `${header}\n${content}`;
                }).join('\n\n') + '\n\n';
            }

            if (soapData.plan.length > 0) {
                organizedContent += 'PLAN\n\n';
                organizedContent += soapData.plan.map(sub => {
                    const header = sub.header.replace(/\*\*/g, '');
                    const content = sub.content.join('\n');
                    return `${header}\n${content}`;
                }).join('\n\n');
            }
        } else {
            // Fallback to original content if SOAP parsing fails
            organizedContent = currentText;
        }

        const htmlContent = organizedContent.split('\n').map(line => {
            const trimmedLine = line.trim();
            if (trimmedLine === 'SUBJECTIVE' || trimmedLine === 'OBJECTIVE' || trimmedLine === 'ASSESSMENT' || trimmedLine === 'PLAN') {
                return `<b style="background: none; background-color: transparent; font-size: 1.2em;">${line}</b>`;
            }
            if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                return `<b style="background: none; background-color: transparent;">${trimmedLine.slice(2, -2)}</b>`;
            }
            return `<span style="background: none; background-color: transparent;">${line}</span>`;
        }).join('<br>');

        const wrappedHtml = `
            <div style="color: black; background: none; background-color: transparent;">
                ${htmlContent}
            </div>
        `;

        const clipboardData = new ClipboardItem({
            'text/html': new Blob([wrappedHtml], { type: 'text/html' }),
            'text/plain': new Blob([organizedContent], { type: 'text/plain' })
        });

        navigator.clipboard.write([clipboardData])
            .then(() => {
                setCopyButtonText('Copied!');
                setTimeout(() => {
                    setCopyButtonText('Copy to Clipboard');
                }, 2000);
            });
    };

    const handleLoadReport = (report) => {
        // Clear any existing form data first
        localStorage.clear();

        // Store individual form fields in localStorage
        if (report.form_data) {
            const formData = report.form_data;
            Object.entries(formData).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    if (typeof value === 'object') {
                        localStorage.setItem(key, JSON.stringify(value));
                    } else {
                        localStorage.setItem(key, value);
                    }
                }
            });
        }

        // Store the report text and ID
        localStorage.setItem('currentReportText', report.report_text);
        localStorage.setItem('currentReportId', report.id);
        localStorage.setItem('previewVisible', 'true');
        localStorage.setItem('patientInfoSubmitted', 'true');

        // Navigate to the report form (PetSOAP Generator)
        navigate('/dashboard/report-form');
    };

    const handleLoadQuickSOAP = (report) => {
        loadNoteIntoQuickSOAP(report);
        navigate('/dashboard/quicksoap');
    };


    // QuickSOAP View Component - renders SOAP sections like QuickSOAP does
    const QuickSOAPView = ({ reportText, isEditable = false, onContentChange }) => {
        const [parsedReport, setParsedReport] = useState(() => parseSOAPReport(reportText));
        const [copiedSection, setCopiedSection] = useState(null);
        const quickSOAPTextareaRefs = useRef({});

        useEffect(() => {
            setParsedReport(parseSOAPReport(reportText));
        }, [reportText]);

        const copySection = async (sectionName, content) => {
            try {
                await navigator.clipboard.writeText(content);
                setCopiedSection(sectionName);
                setTimeout(() => setCopiedSection(null), 2000);
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        };

        const adjustTextareaHeight = (index, textarea) => {
            if (!textarea) {
                textarea = quickSOAPTextareaRefs.current[`section-${index}`];
            }
            if (!textarea) return;
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        };

        if (!parsedReport || parsedReport.sections.length === 0) {
            return (
                <div className="p-8 text-center text-gray-500">
                    <p>No SOAP sections found in this report.</p>
                </div>
            );
        }

        return (
            <div className="max-w-5xl mx-auto">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                    {parsedReport.sections.map((section, index) => {
                        const colors = getSectionColor(section.name);

                        return (
                            <div
                                key={index}
                                className={`border-l-4 ${index < parsedReport.sections.length - 1 ? 'border-b border-gray-200' : ''}`}
                                style={{ borderLeftColor: colors.border }}
                            >
                                {/* Section Header */}
                                <div className={`${colors.header} px-6 py-3 flex items-center justify-between`}>
                                    <h3 className="text-white font-semibold text-lg tracking-wide">
                                        {section.name.charAt(0)} – {section.name}
                                    </h3>
                                    <button
                                        onClick={() => copySection(section.name, section.content)}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${copiedSection === section.name
                                            ? 'bg-white text-blue-600'
                                            : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                                            }`}
                                    >
                                        <FaCopy className="text-xs" />
                                        {copiedSection === section.name ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>

                                {/* Section Content */}
                                <div className={`${colors.bg} px-6 py-4`}>
                                    {isEditable ? (
                                        <textarea
                                            ref={(el) => {
                                                if (el) {
                                                    quickSOAPTextareaRefs.current[`section-${index}`] = el;
                                                    setTimeout(() => adjustTextareaHeight(index, el), 0);
                                                }
                                            }}
                                            value={section.content}
                                            onChange={(e) => {
                                                const newSections = [...parsedReport.sections];
                                                newSections[index].content = e.target.value;
                                                setParsedReport(prev => ({ ...prev, sections: newSections }));
                                                
                                                // Update full report string
                                                const newReport = newSections
                                                    .map(s => `${s.name}:\n${s.content}`)
                                                    .join('\n\n');
                                                
                                                if (onContentChange) {
                                                    onContentChange(newReport);
                                                }
                                                
                                                adjustTextareaHeight(index, e.target);
                                            }}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none text-gray-800 leading-relaxed bg-white text-sm"
                                            style={{
                                                fontFamily: 'inherit',
                                                lineHeight: '1.6',
                                                height: 'auto',
                                                overflowY: 'auto',
                                                whiteSpace: 'pre-wrap',
                                                wordWrap: 'break-word'
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-800 leading-relaxed text-sm whitespace-pre-wrap">
                                            {section.content}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Handle SOAP section copy functionality
    const handleCopySection = async (sectionContent, sectionTitle) => {
        try {
            // Include the section title at the top
            const contentWithTitle = `${sectionTitle.toUpperCase()}\n\n${sectionContent}`;

            // Create both HTML and plain text versions
            const htmlContent = contentWithTitle.split('\n').map(line => {
                const trimmedLine = line.trim();
                if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                    return `<b style="background: none; background-color: transparent;">${trimmedLine.slice(2, -2)}</b>`;
                }
                return `<span style="background: none; background-color: transparent;">${line}</span>`;
            }).join('<br>');

            const wrappedHtml = `
                <div style="color: black; background: none; background-color: transparent;">
                    ${htmlContent}
                </div>
            `;

            const plainText = contentWithTitle.replace(/\*\*/g, '');

            const clipboardData = new ClipboardItem({
                'text/html': new Blob([wrappedHtml], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' })
            });

            await navigator.clipboard.write([clipboardData]);

            // Show success message
            setCopyButtonText(`${sectionTitle} Copied!`);
            setTimeout(() => {
                setCopyButtonText('Copy to Clipboard');
            }, 2000);
        } catch (err) {
            console.error('Copy failed:', err);
            setCopyButtonText('Copy Failed');
            setTimeout(() => setCopyButtonText('Copy to Clipboard'), 2000);
        }
    };

    if (isLoading) return <div>Loading...</div>; // Handle loading state

    if (!isAuthenticated) {
        return <div>Please log in to view your saved reports.</div>;
    }

    const handleDismissMobileSOAPAlert = () => {
        setHasNewMobileSOAP(false);
        setNewMobileSOAPId(null);
        localStorage.removeItem('hasNewMobileSOAP');
        localStorage.removeItem('newMobileSOAPId');
    };

    // Helper function to get record type badge color
    const getRecordTypeBadgeColor = (recordType) => {
        if (recordType === 'quicksoap' || recordType === 'soap') {
            return 'bg-gradient-to-r from-primary-600 to-primary-700';
        } else if (recordType === 'generator') {
            return 'bg-gradient-to-r from-purple-500 to-purple-700';
        } else if (recordType === 'summary') {
            return 'bg-gradient-to-r from-emerald-500 to-emerald-700';
        } else if (recordType === 'callback') {
            return 'bg-gradient-to-r from-amber-500 to-amber-700';
        }
        return 'bg-gradient-to-r from-gray-500 to-gray-700';
    };

    // Helper function to get record type display name
    const getRecordTypeDisplayName = (recordType) => {
        if (recordType === 'quicksoap' || recordType === 'soap') return 'QuickSOAP';
        if (recordType === 'generator') return 'PetSOAP';
        if (recordType === 'summary') return 'Summary';
        if (recordType === 'callback') return 'Callback';
        return 'Record';
    };

    return (
        <div className="saved-reports" style={{ backgroundColor: 'transparent', minHeight: '100vh' }}>
            <div className="max-w-6xl mx-auto px-6 py-8">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-6">Saved Records</h2>
                {(error || listError) && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                        <p className="text-red-600 text-sm">{error || listError}</p>
                    </div>
                )}

                {!selectedReport && (
                    <div className="mb-6 flex gap-4 items-center">
                        <div className="flex-1 bg-white rounded-xl shadow-md border border-gray-200">
                            <input
                                type="text"
                                placeholder="Search by patient, date or text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                            />
                        </div>
                        {/* Filter Buttons */}
                        <div className="bg-gray-100 rounded-xl p-1 inline-flex gap-1">
                            <button
                                onClick={() => setFilterType('all')}
                                className={`px-3 py-2.5 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                                    filterType === 'all' 
                                        ? 'bg-white text-primary-600 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilterType('quicksoap')}
                                className={`px-3 py-2.5 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                                    filterType === 'quicksoap' 
                                        ? 'bg-white text-primary-600 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                }`}
                            >
                                QuickSOAP
                            </button>
                            <button
                                onClick={() => setFilterType('petsoap')}
                                className={`px-3 py-2.5 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                                    filterType === 'petsoap' 
                                        ? 'bg-white text-primary-600 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                }`}
                            >
                                PetSOAP
                            </button>
                            <button
                                onClick={() => setFilterType('summary')}
                                className={`px-3 py-2.5 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                                    filterType === 'summary' 
                                        ? 'bg-white text-primary-600 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                }`}
                            >
                                Summary
                                <span className="text-[7px] font-medium text-yellow-500">beta</span>
                            </button>
                            <button
                                onClick={() => setFilterType('callback')}
                                className={`px-3 py-2.5 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                                    filterType === 'callback' 
                                        ? 'bg-white text-primary-600 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                }`}
                            >
                                Callback
                                <span className="text-[7px] font-medium text-yellow-500">beta</span>
                            </button>
                        </div>
                    </div>
                )}

                <div className={`${selectedReport ? 'hidden' : ''} transition-opacity ${(isLoadingReports || searching) && reports.length > 0 ? 'opacity-60' : ''}`}>
                    {isLoadingReports && reports.length === 0 ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
                        </div>
                    ) : reports.length > 0 ? (
                        <div className="grid gap-4">
                            {reports.map((report, index) => {
                                const isUnopenedMobile = isUnopenedMobileReport(report);
                                const isUnopenedDesktop = isUnopenedDesktopQuickSOAP(report);
                                const isUnopened = isUnopenedMobile || isUnopenedDesktop;
                                const recordType = getRecordType(report);
                                const badgeColor = getRecordTypeBadgeColor(recordType);
                                return (
                                <div 
                                    key={report.id} 
                                    className={`rounded-xl shadow-md hover:shadow-lg border transition-all duration-200 p-5 cursor-pointer group ${
                                        isUnopened 
                                            ? 'bg-blue-100 border-blue-400' 
                                            : 'bg-white border-gray-200'
                                    }`}
                                    onClick={() => handleReportClick(report)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {editingIndex === index ? (
                                                <input
                                                    type="text"
                                                    value={newReportName}
                                                    onChange={handleNameChange}
                                                    onBlur={() => handleNameBlur(report.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    autoFocus
                                                />
                                            ) : (
                                                <>
                                                    <div className={`${badgeColor} text-white text-xs font-semibold px-3 py-1 rounded-lg uppercase tracking-wide flex-shrink-0`}>
                                                        {getRecordTypeDisplayName(recordType)}
                                                    </div>
                                                    <span className={`font-medium text-base flex-1 min-w-0 truncate transition-colors ${
                                                        isUnopened 
                                                            ? 'text-blue-900 group-hover:text-blue-950 font-semibold' 
                                                            : 'text-gray-800 group-hover:text-primary-600'
                                                    }`}>
                                                        {report.report_name}
                                                    </span>
                                                    {isUnopened && (
                                                        <div className="w-2.5 h-2.5 bg-blue-600 rounded-full animate-pulse flex-shrink-0" title="New mobile SOAP report"></div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                            <button
                                                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditClick(index);
                                                }}
                                                title="Edit name"
                                            >
                                                <FaEdit className="text-sm" />
                                            </button>
                                            <button
                                                className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteReport(report.id);
                                                }}
                                                title="Delete report"
                                            >
                                                <FaTimes className="text-sm" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                            <p className="text-gray-500 text-lg">
                                {searchTerm.trim() ? `No records match "${searchTerm.trim()}".` : 'No saved reports available.'}
                            </p>
                        </div>
                    )}
                    {hasMore && reports.length > 0 && (
                        <div className="flex justify-center mt-6">
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm text-sm font-medium text-gray-700 hover:border-primary-500 hover:text-primary-600 transition-colors disabled:opacity-60"
                            >
                                {loadingMore && <span className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-primary-600 animate-spin" />}
                                {loadingMore ? 'Loading' : 'Load more'}
                            </button>
                        </div>
                    )}
                </div>

                {selectedReport && (
                    <div className="report-card">
                    <div className="report-card-header">
                        <div className="header-top">
                            <h3>{selectedReport.report_name}</h3>
                            <div className="view-controls">
                                <div className="view-toggle-buttons">
                                    <button
                                        className={`view-toggle-btn ${currentView === 'soap' ? 'active' : ''}`}
                                        onClick={() => setCurrentView('soap')}
                                    >
                                        SOAP
                                    </button>
                                    <button
                                        className={`view-toggle-btn ${currentView === 'standard' ? 'active' : ''}`}
                                        onClick={() => setCurrentView('standard')}
                                    >
                                        Standard
                                    </button>
                                </div>
                                <button
                                    className="close-button"
                                    onClick={handleCloseReport}
                                    title="Close report"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                        <div className="action-buttons">
                            <div className="primary-actions">
                                {(() => {
                                    const recordType = getRecordType(selectedReport);
                                    return (
                                        <>
                                            {recordType === 'generator' && (
                                                <button
                                                    className="load-button"
                                                    onClick={() => handleLoadReport(selectedReport)}
                                                    title="Load report in generator"
                                                >
                                                    Load in Generator
                                                </button>
                                            )}
                                            {recordType === 'quicksoap' && (
                                                <button
                                                    className="load-button"
                                                    onClick={() => handleLoadQuickSOAP(selectedReport)}
                                                    title="Load report in QuickSOAP"
                                                >
                                                    Load in QuickSOAP
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                                {editingReport?.id === selectedReport.id ? (
                                    <>
                                        <button
                                            className="save-button"
                                            onClick={() => handleSaveEdit(editingReport)}
                                        >
                                            Save
                                        </button>
                                        <button
                                            className="cancel-button"
                                            onClick={handleCancelEdit}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className="edit-button"
                                        onClick={() => setEditingReport({ ...selectedReport })}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>
                            <div className="secondary-actions">
                                <div className="copy-button-container">
                                    <button className="copy-button" onClick={copyToClipboard}>
                                        {copyButtonText}
                                    </button>
                                </div>
                                <PDFButton
                                    reportText={editingReport?.id === selectedReport.id ? editingReport.report_text : selectedReport.report_text}
                                    reportName={selectedReport.report_name}
                                />
                                <PrintButton
                                    reportText={editingReport?.id === selectedReport.id ? editingReport.report_text : selectedReport.report_text}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="report-content">
                        {currentView === 'soap' ? (
                            (() => {
                                const recordType = getRecordType(selectedReport);
                                const reportText = editingReport?.id === selectedReport.id ? editingReport.report_text : selectedReport.report_text;
                                
                                // Summary and Callback records use simple textarea view
                                if (recordType === 'summary' || recordType === 'callback') {
                                    return (
                                        <div className="editor-wrapper" style={{ padding: '16px', backgroundColor: '#f8fafc' }}>
                                            <div className={`px-4 py-2 rounded-t-lg ${recordType === 'summary' ? 'bg-emerald-600' : 'bg-amber-600'}`}>
                                                <h3 className="text-white font-semibold text-lg tracking-wide">
                                                    {recordType === 'summary' ? '📋 Visit Summary' : '📞 Callback Notes'}
                                                </h3>
                                            </div>
                                            {editingReport?.id === selectedReport.id ? (
                                                <textarea
                                                    value={reportText}
                                                    onChange={(e) => {
                                                        setEditingReport({
                                                            ...editingReport,
                                                            report_text: e.target.value
                                                        });
                                                    }}
                                                    className="w-full px-4 py-4 border border-gray-300 border-t-0 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none text-gray-800 leading-relaxed bg-white"
                                                    style={{
                                                        fontFamily: 'inherit',
                                                        lineHeight: '1.7',
                                                        minHeight: '400px',
                                                        whiteSpace: 'pre-wrap',
                                                        wordWrap: 'break-word'
                                                    }}
                                                />
                                            ) : (
                                                <div 
                                                    className="w-full px-4 py-4 border border-gray-300 border-t-0 rounded-b-lg bg-white text-gray-800"
                                                    style={{
                                                        fontFamily: 'inherit',
                                                        lineHeight: '1.7',
                                                        minHeight: '400px',
                                                        whiteSpace: 'pre-wrap',
                                                        wordWrap: 'break-word'
                                                    }}
                                                >
                                                    {reportText}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                
                                if (recordType === 'quicksoap' || recordType === 'soap') {
                                    return (
                                        <div className="editor-wrapper" style={{ padding: '16px', backgroundColor: '#f8fafc' }}>
                                            <QuickSOAPView
                                                reportText={reportText}
                                                isEditable={editingReport?.id === selectedReport.id}
                                                onContentChange={editingReport?.id === selectedReport.id ? (newText) => {
                                                    setEditingReport({
                                                        ...editingReport,
                                                        report_text: newText
                                                    });
                                                } : undefined}
                                            />
                                        </div>
                                    );
                                } else {
                                    return (
                                        <SOAPView
                                            reportText={reportText}
                                            onCopySection={handleCopySection}
                                            isEditable={editingReport?.id === selectedReport.id}
                                            onContentChange={editingReport?.id === selectedReport.id ? (newText) => {
                                                setEditingReport({
                                                    ...editingReport,
                                                    report_text: newText
                                                });
                                            } : undefined}
                                            forceTextView={true}
                                        />
                                    );
                                }
                            })()
                        ) : (
                            <div className="editor-wrapper">
                                {editingReport?.id === selectedReport.id ? (
                                    <Slate
                                        editor={editor}
                                        value={deserializeSlateValue(editingReport.report_text)}
                                        onChange={value => {
                                            const newText = processSlateForPDF(value);
                                            setEditingReport({
                                                ...editingReport,
                                                report_text: newText
                                            });
                                        }}
                                    >
                                        <Editable
                                            renderElement={renderElement}
                                            renderLeaf={renderLeaf}
                                            style={{
                                                minHeight: '100%',
                                                padding: '16px',
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: '1.2',
                                                fontSize: '14px'
                                            }}
                                            onCopy={(event) => {
                                                event.preventDefault();
                                                const selection = window.getSelection();

                                                const selectedNodes = deserializeSlateValue(editingReport.report_text).filter(node => {
                                                    const nodeText = Node.string(node);
                                                    return selection.toString().includes(nodeText);
                                                });

                                                const formattedText = processSlateForPDF(selectedNodes);
                                                const plainText = formattedText.replace(/\*\*/g, '');

                                                const htmlContent = formattedText.split('\n').map(line => {
                                                    if (line.startsWith('**') && line.endsWith('**')) {
                                                        return `<b style="background: none; background-color: transparent;">${line.slice(2, -2)}</b>`;
                                                    }
                                                    return `<span style="background: none; background-color: transparent;">${line}</span>`;
                                                }).join('<br>');

                                                const wrappedHtml = `
                                                    <div style="color: black; background: none; background-color: transparent;">
                                                        ${htmlContent}
                                                    </div>
                                                `;

                                                event.clipboardData.setData('text/html', wrappedHtml);
                                                event.clipboardData.setData('text/plain', plainText);
                                            }}
                                        />
                                    </Slate>
                                ) : (
                                    <Slate
                                        editor={editor}
                                        initialValue={deserializeSlateValue(selectedReport.report_text)}
                                        value={deserializeSlateValue(selectedReport.report_text)}
                                        onChange={() => { }}
                                    >
                                        <Editable
                                            readOnly
                                            renderElement={renderElement}
                                            renderLeaf={renderLeaf}
                                            style={{
                                                minHeight: '100%',
                                                padding: '16px',
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: '1.2',
                                                fontSize: '14px'
                                            }}
                                            onCopy={(event) => {
                                                event.preventDefault();
                                                const selection = window.getSelection();

                                                const selectedNodes = deserializeSlateValue(selectedReport.report_text).filter(node => {
                                                    const nodeText = Node.string(node);
                                                    return selection.toString().includes(nodeText);
                                                });

                                                const formattedText = processSlateForPDF(selectedNodes);
                                                const plainText = formattedText.replace(/\*\*/g, '');

                                                const htmlContent = formattedText.split('\n').map(line => {
                                                    if (line.startsWith('**') && line.endsWith('**')) {
                                                        return `<b style="background: none; background-color: transparent;">${line.slice(2, -2)}</b>`;
                                                    }
                                                    return `<span style="background: none; background-color: transparent;">${line}</span>`;
                                                }).join('<br>');

                                                const wrappedHtml = `
                                                    <div style="color: black; background: none; background-color: transparent;">
                                                        ${htmlContent}
                                                    </div>
                                                `;

                                                event.clipboardData.setData('text/html', wrappedHtml);
                                                event.clipboardData.setData('text/plain', plainText);
                                            }}
                                        />
                                    </Slate>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default SavedReports;